import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api from "../../api";
import toast from "react-hot-toast";

// Async thunks
export const loginUser = createAsyncThunk(
  "auth/login",
  async (credentials, { rejectWithValue }) => {
    // Basic validation
    if (!credentials.email || !credentials.password) {
      return rejectWithValue("Email and password are required");
    }
    try {
      const response = await api.post("api/login/", {
        email: credentials.email,
        password: credentials.password,
        // The trusted-device token now lives in an HttpOnly cookie that the
        // browser sends automatically (withCredentials). We still forward any
        // legacy localStorage token as a one-time bridge so devices remembered
        // before this change aren't re-challenged on their next login.
        trusted_device_token: localStorage.getItem("crm_trusted_device") || undefined,
      });
      // 2FA-enabled users don't get a session here — they get a challenge and
      // must complete /api/login/2fa/verify/ first.
      if (response.data.two_factor_required) {
        return {
          twoFactorRequired: true,
          challengeToken: response.data.challenge_token,
          maskedPhone: response.data.masked_phone,
          email: response.data.email,
          // 'totp' = the code is already on their phone in an authenticator
          // app, so the verify screen must not claim we texted them.
          method: response.data.method || "sms",
          smsFallbackAvailable: !!response.data.sms_fallback_available,
        };
      }
      return {
        user: response.data.user || response.data,
        access: response.data.access || null,
      };
    } catch (error) {
      let message = "Login failed";
      if (error.response && error.response.data && error.response.data.detail) {
        message = error.response.data.detail;
        if (error.response.data.email_sent) {
          toast(
            "A verification link has been sent to your inbox. Please check your email to complete account setup.",
            {
              icon: "✉️",
              duration: 7000,
              style: {
                background: "#0e7490",
                color: "#fff",
                fontWeight: "500",
                borderRadius: "12px",
                padding: "14px 18px",
                maxWidth: "420px",
              },
            }
          );
        } else {
          toast.error(message);
        }
      } else if (error.message) {
        message = error.message;
        toast.error(message);
      }
      return rejectWithValue(message);
    }
  }
);

// Step 2 of a 2FA login: submit the SMS code against the challenge token.
export const verifyTwoFactor = createAsyncThunk(
  "auth/verifyTwoFactor",
  async ({ challengeToken, otp, rememberDevice }, { rejectWithValue }) => {
    try {
      const response = await api.post("api/login/2fa/verify/", {
        challenge_token: challengeToken,
        otp,
        remember_device: !!rememberDevice,
      });
      // The server sets an HttpOnly cookie AND returns the trusted-device token
      // in the body. We keep the body copy in localStorage as a fallback: in
      // production the CRM talks to the API cross-site, where Safari/iOS and
      // third-party-cookie blocking drop the cookie, so the cookie alone would
      // never come back and 2FA would re-prompt every login. loginUser forwards
      // this value so a remembered device is recognised either way.
      if (rememberDevice && response.data.trusted_device_token) {
        localStorage.setItem("crm_trusted_device", response.data.trusted_device_token);
      } else if (!rememberDevice) {
        localStorage.removeItem("crm_trusted_device");
      }
      return {
        user: response.data.user || response.data,
        access: response.data.access || null,
      };
    } catch (error) {
      const message =
        error?.response?.data?.detail || error?.message || "Verification failed";
      toast.error(message);
      return rejectWithValue(message);
    }
  }
);

// Re-send the SMS code for an in-progress 2FA login.
export const resendTwoFactor = createAsyncThunk(
  "auth/resendTwoFactor",
  async ({ challengeToken }, { rejectWithValue }) => {
    try {
      const response = await api.post("api/login/2fa/resend/", {
        challenge_token: challengeToken,
      });
      toast.success(response.data?.detail || "A new code has been sent.");
      return { maskedPhone: response.data?.masked_phone || null };
    } catch (error) {
      const message =
        error?.response?.data?.detail || error?.message || "Couldn't resend the code";
      toast.error(message);
      return rejectWithValue(message);
    }
  }
);

export const logoutUser = createAsyncThunk(
  "auth/logout",
  async (_, { rejectWithValue }) => {
    try {
      await api.post("api/logout/");
    } catch (error) {
      console.error("Logout failed", error);
    }
    return;
  }
);

const initialState = {
  user: JSON.parse(localStorage.getItem("user")) || null,
  status: "idle",
  error: null,
  // Pending 2FA challenge: { challengeToken, maskedPhone, email }. Non-null
  // means the login page should show the OTP verification step.
  twoFactor: null,
};

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    // Synchronous logout (clears state only)
    logout: (state) => {
      state.user = null;
      state.status = "idle";
      state.error = null;
      state.twoFactor = null;
      localStorage.removeItem("user");
      localStorage.removeItem("token");
      localStorage.removeItem("accessToken");
      localStorage.removeItem("refreshToken");
      localStorage.removeItem("jwt_access");

      // Dispatch custom event to notify chatbot to clear history
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('user-logout'));
      }
    },
    // Replace the cached user (e.g., after profile picture / name update)
    setUser: (state, action) => {
      state.user = action.payload;
      try {
        localStorage.setItem("user", JSON.stringify(action.payload));
      } catch (_) {}
    },
    // Cancel a pending 2FA challenge (user hit "Back to login").
    clearTwoFactor: (state) => {
      state.twoFactor = null;
      state.status = "idle";
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Login cases
      .addCase(loginUser.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.error = null;
        // 2FA-enabled login: hold the challenge, don't log in yet.
        if (action.payload.twoFactorRequired) {
          state.twoFactor = {
            challengeToken: action.payload.challengeToken,
            maskedPhone: action.payload.maskedPhone,
            email: action.payload.email,
            method: action.payload.method,
            smsFallbackAvailable: action.payload.smsFallbackAvailable,
          };
          return;
        }
        state.twoFactor = null;
        state.user = action.payload.user;
        localStorage.setItem("user", JSON.stringify(action.payload.user));
        if (action.payload.access) {
          localStorage.setItem("jwt_access", action.payload.access);
        }
        localStorage.removeItem("token");
        localStorage.removeItem("accessToken");
        localStorage.removeItem("refreshToken");
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload || "Login failed";
      })
      // 2FA verify cases — completes the login once the code is confirmed.
      .addCase(verifyTwoFactor.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(verifyTwoFactor.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.error = null;
        state.twoFactor = null;
        state.user = action.payload.user;
        localStorage.setItem("user", JSON.stringify(action.payload.user));
        if (action.payload.access) {
          localStorage.setItem("jwt_access", action.payload.access);
        }
        localStorage.removeItem("token");
        localStorage.removeItem("accessToken");
        localStorage.removeItem("refreshToken");
      })
      .addCase(verifyTwoFactor.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload || "Verification failed";
      })
      // Resend just refreshes the masked phone; no state change on failure.
      .addCase(resendTwoFactor.fulfilled, (state, action) => {
        if (state.twoFactor && action.payload?.maskedPhone) {
          state.twoFactor.maskedPhone = action.payload.maskedPhone;
        }
        // An authenticator user who asked to be texted instead is now waiting on
        // a text, so the screen should say so. The backend accepts either code
        // from here on, but the instruction on screen has to match what they did.
        if (state.twoFactor) {
          state.twoFactor.method = "sms";
        }
      })
      // Logout cases
      .addCase(logoutUser.fulfilled, (state) => {
        state.user = null;
        state.status = "idle";
        state.twoFactor = null;
        localStorage.removeItem("user");
        localStorage.removeItem("token");
        localStorage.removeItem("accessToken");
        localStorage.removeItem("refreshToken");
        localStorage.removeItem("jwt_access");
      });
  },
});

export const { logout, setUser, clearTwoFactor } = authSlice.actions;
export default authSlice.reducer;
